// Single source of truth for turning a backend error payload into text the UI
// can show. Pure JS: no React, no i18n — the caller supplies the translated
// fallback. Handles every shape the backends behind this front actually emit:
// Nest ValidationPipe arrays, FastAPI 422 detail lists, the pickups/MercadoLibre
// `{ok:false, error}` envelope, axios errors and Cloudflare HTML pages.

import axios from 'axios';

// Bodies and transport errors that carry no information for the user.
const GENERIC_MESSAGES = [
  'bad request',
  'unauthorized',
  'forbidden',
  'forbidden resource', // Nest default when a guard returns false
  'not found',
  'conflict',
  'unprocessable entity',
  'too many requests',
  'throttlerexception: too many requests',
  'internal server error',
  'bad gateway',
  'service unavailable',
  'gateway timeout',
  'unknown error',
  'network error',
  'canceled',
];

// Default 404 of an undeployed Nest route, axios transport messages, etc.
const GENERIC_PATTERNS = [
  /^Request failed with status code \d+$/i,
  /^timeout of \d+ms exceeded$/i,
  /^Cannot (GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS) \//i,
];

// Raw exception text some backends forward on 5xx (e.g. microservice-reports
// sends `exception.message`, the helpdesk AI proxy sends axios' message in
// `detail`). It leaks internal hosts and says nothing to the user, so it is
// never shown; plain business sentences on a 5xx are still shown.
const TECHNICAL_PATTERNS = [
  // Node network errors.
  /\b(ECONNREFUSED|ENOTFOUND|ETIMEDOUT|ECONNRESET|EAI_AGAIN|EHOSTUNREACH|EPIPE)\b/,
  /\bsocket hang up\b/i,
  /\bgetaddrinfo\b/i,
  // IPv4 with port, and host:port (the host must start with a letter, so a time
  // like '12:00' is not taken for one).
  /\b\d{1,3}(\.\d{1,3}){3}:\d{1,5}\b/,
  /\b(localhost|[a-z][\w-]*(\.[\w-]+)+):\d{2,5}\b/i,
  /\b[a-z][\w-]*:\d{4,5}\b/i,
  // MongoDB / Mongoose.
  /\b(Mongo\w*Error|Mongoose\w*Error|CastError|E11000)\b/,
  /\bCast to \w+ failed\b/i,
  // Mongoose / driver `err.message` as a Nest filter forwards it: the class
  // name (MongoServerError…) lives in err.name, never in the message.
  /\bbuffering timed out after \d+ms\b/i,
  /\bServer selection timed out\b/i,
  /\bnot authorized on \S+ to execute command\b/i,
  /\bTopology (is )?closed\b/i,
  /\bClient must be connected\b/i,
  /\bcaused by ::/,
  // JavaScript runtime errors (explicit class prefix, or V8's bare message).
  /\b(TypeError|ReferenceError|RangeError|SyntaxError)\b/,
  /\bCannot (read|set) propert(y|ies) of\b/i,
  /\bCannot destructure property\b/i,
  /\bis not a function\b/,
  /\bis not iterable\b/,
  /^[\w$.]+ is not defined$/,
  /\b(undefined|null) is not an object\b/,
  /^Invalid time value$/,
  /\bCannot access '.+' before initialization\b/,
  // JSON.parse failures, old ('Unexpected token u in JSON at position 0') and
  // Node 20+ ('… "undefined" is not valid JSON', 'Unexpected end of JSON input').
  /\bUnexpected token .+ in JSON\b/i,
  /\bis not valid JSON\b/i,
  /\bUnexpected end of JSON input\b/i,
  // Stack trace line: 'at handler (/app/dist/main.js:10:5)'.
  /^\s*at\s+.+:\d+:\d+\)?\s*$/m,
  /^internal server error\b/i,
  // HTML anywhere in the text (a proxy error page).
  /<(!doctype|html|head|body)\b/i,
];

// FastAPI prefixes `loc` with the request part; the user only cares about the
// field path that follows.
const LOC_SOURCE_SEGMENTS = ['body', 'query', 'path', 'header'];

const isUsableText = (text) => {
  if (typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('<')) return false; // HTML from a proxy
  if (GENERIC_MESSAGES.includes(trimmed.toLowerCase())) return false;
  if (GENERIC_PATTERNS.some((pattern) => pattern.test(trimmed))) return false;
  return !TECHNICAL_PATTERNS.some((pattern) => pattern.test(trimmed));
};

/** `{loc:['body','ollama','temperature'], msg:'…'}` → 'ollama.temperature: …' */
const formatLocatedMessage = (item) => {
  const msg = typeof item.msg === 'string' ? item.msg.trim() : '';
  if (!msg) return null;
  const loc = Array.isArray(item.loc) ? item.loc : [];
  const segments = LOC_SOURCE_SEGMENTS.includes(loc[0]) ? loc.slice(1) : loc;
  const path = segments
    .filter((segment) => segment !== null && segment !== undefined)
    .map((segment) => String(segment))
    .join('.');
  return path ? `${path}: ${msg}` : msg;
};

/** One raw message, without filtering generic phrases. null when empty. */
const rawItem = (item) => {
  if (typeof item === 'string') return item.trim() || null;
  if (item && typeof item === 'object') {
    if (typeof item.msg === 'string') return formatLocatedMessage(item);
    if (typeof item.message === 'string') return item.message.trim() || null;
  }
  return null;
};

/** One message that is safe to show, or null. */
const usableItem = (item) => {
  const message = rawItem(item);
  return message && isUsableText(message) ? message : null;
};

const bodyMessage = (body, depth) => {
  if (depth > 5) return null; // defensive: nested {message:{message:…}}
  if (typeof body === 'string') return isUsableText(body) ? body.trim() : null;
  if (Array.isArray(body)) {
    const messages = body.map(usableItem).filter(Boolean);
    return messages.length ? messages.join(' · ') : null;
  }
  if (!body || typeof body !== 'object') return null;
  return (
    bodyMessage(body.message, depth + 1) ??
    bodyMessage(body.detail, depth + 1) ??
    (typeof body.error === 'string' && isUsableText(body.error) ? body.error.trim() : null)
  );
};

/**
 * Human text carried by a response body, or null when the payload has none
 * (empty, HTML from a proxy, or a bare status phrase like 'Forbidden').
 * @param {unknown} body - parsed response body
 * @returns {string|null}
 */
export function apiBodyMessage(body) {
  return bodyMessage(body, 0);
}

/**
 * Raw messages of a response body, without joining and without dropping
 * generic phrases. Returns [] when there is nothing.
 * @param {unknown} body - parsed response body
 * @returns {string[]}
 */
export function apiBodyMessages(body) {
  if (!body || typeof body !== 'object') return [];
  const collect = (value) => {
    if (Array.isArray(value)) return value.map(rawItem).filter(Boolean);
    if (typeof value === 'string' && value.trim()) return [value.trim()];
    return [];
  };
  const messages = collect(body.message);
  if (messages.length) return messages;
  const details = collect(body.detail);
  if (details.length) return details;
  if (typeof body.error === 'string' && body.error.trim()) return [body.error.trim()];
  return [];
}

/**
 * The response body of any error shape this app produces (axios error,
 * errorInfo returned by handleApiError, or a plain body).
 * @param {unknown} err
 * @returns {unknown|null}
 */
export function apiErrorData(err) {
  if (!err || typeof err !== 'object') return null;
  if (err.response) return err.response.data ?? null;
  // errorInfo shape returned by errorHandler.handleApiError(): its `message` is
  // already UI text, so only `data` (the backend body) is read — and an
  // errorInfo without body yields null, never its own message.
  if ('originalError' in err) return err.data ?? null;
  if (!(err instanceof Error)) return err;
  return null;
}

/**
 * Shape only throwFetchApiError produces (an Error carrying the raw body
 * fields). It has no `response`, so the body is not reachable through
 * apiErrorData and these helpers read its own fields instead.
 * @param {unknown} err
 * @returns {boolean}
 */
const isFetchApiError = (err) =>
  err instanceof Error && Array.isArray(err.messages) && 'serverMessage' in err;

/**
 * Backend text for an error, falling back to `fallback` when there is none.
 * Never returns transport noise ('Network Error', 'Request failed…') nor
 * throwFetchApiError's raw `message` ('Forbidden', 'Error 502').
 * @param {unknown} err
 * @param {string|null} [fallback]
 * @returns {string|null}
 */
export function apiErrorMessage(err, fallback = null) {
  if (isFetchApiError(err)) {
    return typeof err.serverMessage === 'string' && err.serverMessage ? err.serverMessage : fallback;
  }
  const fromData = apiBodyMessage(apiErrorData(err));
  if (fromData) return fromData;
  if (typeof err === 'string') {
    const fromString = apiBodyMessage(err);
    if (fromString) return fromString;
  }
  return fallback;
}

/**
 * HTTP status of an error, or null when it isn't a number. An errorInfo with
 * status null (network) or 'CORS_ERROR' also yields null: use isNetworkError()
 * to tell that case apart.
 * @param {unknown} err
 * @returns {number|null}
 */
export function apiErrorStatus(err) {
  if (!err || typeof err !== 'object') return null;
  if (typeof err.response?.status === 'number') return err.response.status;
  // errorInfo keeps `status` at the top level ('CORS_ERROR' or null).
  if (typeof err.status === 'number') return err.status;
  if (typeof err.statusCode === 'number') return err.statusCode;
  return null;
}

/**
 * Backend `code` of the body (e.g. AI_CAPABILITY_NOT_GRANTABLE). Axios own
 * codes ('ERR_BAD_REQUEST') are never returned.
 * @param {unknown} err
 * @returns {string|null}
 */
export function apiErrorCode(err) {
  if (isFetchApiError(err)) return typeof err.code === 'string' ? err.code : null;
  const code = apiErrorData(err)?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * true when the request never reached the server (offline, DNS, CORS).
 * @param {unknown} err
 * @returns {boolean}
 */
export function isNetworkError(err) {
  if (!err || typeof err !== 'object') return false;
  const isCancelled = (error) =>
    Boolean(error) && (axios.isCancel(error) || error.name === 'CanceledError');
  if (isCancelled(err) || isCancelled(err.originalError)) return false;
  if ('originalError' in err) {
    const status = apiErrorStatus(err);
    return status === null || status === 'CORS_ERROR';
  }
  return (err.isAxiosError === true || Boolean(err.request)) && !err.response;
}

/**
 * TextDecoder is not guaranteed in every runtime (jsdom); the fallback is
 * enough for the ASCII/UTF-8 payloads these errors carry.
 */
const decodeBuffer = (buffer) => {
  if (typeof TextDecoder !== 'undefined') return new TextDecoder().decode(buffer);
  const bytes = new Uint8Array(buffer);
  let text = '';
  for (let i = 0; i < bytes.length; i += 1) text += String.fromCharCode(bytes[i]);
  return text;
};

/** Blob.text() is missing in some environments (jsdom) — FileReader covers it. */
const readBlobText = (blob) => {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

/**
 * Like apiErrorMessage but resolves Blob/ArrayBuffer bodies (a download
 * endpoint answering with a JSON error file, for instance). Never throws.
 * @param {unknown} err
 * @param {string|null} [fallback]
 * @returns {Promise<string|null>}
 */
export async function apiErrorMessageAsync(err, fallback = null) {
  try {
    const data = err?.response?.data;
    let text = null;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      text = await readBlobText(data);
    } else if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
      text = decodeBuffer(data);
    }
    if (typeof text === 'string') {
      // A body that parses (even to the literal null) is read as JSON; only
      // unparseable text is read raw, so 'null' never reaches the user.
      let body = text;
      try {
        body = JSON.parse(text);
      } catch {
        body = text;
      }
      const message = apiBodyMessage(body);
      if (message) return message;
    }
    return apiErrorMessage(err) ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Maps class-validator style messages ('packages.3.city.name should not be
 * empty') onto form keys. Only the first message per form key is kept.
 * @param {string[]} messages
 * @param {Record<string,string>} fieldMap - backend path → form key
 * @returns {{errors: Record<string,string>, unmatched: string[]}}
 */
export function mapValidationErrors(messages, fieldMap) {
  const errors = {};
  const unmatched = [];
  if (!Array.isArray(messages)) return { errors, unmatched };
  const routes = Object.keys(fieldMap || {});
  messages.forEach((rawMessage) => {
    if (typeof rawMessage !== 'string') return;
    const message = rawMessage.trim();
    if (!message) return;
    const route = message.split(/\s+/)[0];
    // Longest matching prefix wins: 'packages.3.address' beats 'packages'.
    const match = routes
      .filter((key) => key && (route === key || route.startsWith(`${key}.`)))
      .sort((a, b) => b.length - a.length)[0];
    const formKey = match ? fieldMap[match] : null;
    if (!formKey) {
      unmatched.push(message);
      return;
    }
    if (!(formKey in errors)) errors[formKey] = message;
  });
  return { errors, unmatched };
}

/**
 * Throws the rich Error the helpdesk fetch clients need: `status` to tell
 * "endpoint not deployed" (404/405) apart from a real failure, `code` and
 * `capabilities` for the typed AI 403s, plus `messages` (raw array) and
 * `serverMessage` (null when the body only carried a generic phrase).
 * apiErrorMessage()/apiErrorCode() read this shape back (serverMessage/code),
 * so views can call them directly on a client error.
 * Replaces the five identical copies of throwApiError.
 * @param {Response} response
 * @returns {Promise<never>}
 */
export async function throwFetchApiError(response) {
  const parsed =
    response && typeof response.json === 'function'
      ? await response.json().catch(() => ({}))
      : {};
  const body = parsed && typeof parsed === 'object' ? parsed : {};

  // Same precedence the clients used (detail before message), but only a
  // detail that carries text wins: 0, false, [] or blanks never shadow a
  // usable message.
  const hasDetail = apiBodyMessages({ detail: body.detail }).length > 0;
  const raw = hasDetail ? body.detail : body.message;

  let message;
  if (typeof raw === 'string' && raw.trim()) {
    message = raw;
  } else if (Array.isArray(raw)) {
    const parts = raw.map(rawItem).filter(Boolean);
    message = parts.length ? parts.join(' · ') : `Error ${response.status}`;
  } else {
    message = `Error ${response.status}`;
  }

  const error = new Error(message);
  error.status = response.status;
  if (body.code) error.code = body.code;
  if (Array.isArray(body.capabilities)) error.capabilities = body.capabilities;
  error.messages = hasDetail ? apiBodyMessages({ detail: body.detail }) : apiBodyMessages(body);
  error.serverMessage =
    apiBodyMessage({ detail: body.detail }) ?? apiBodyMessage({ message: body.message }) ?? null;

  throw error;
}
